import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import EditorialReview from "./editorial-review";
import { ArrowIcon } from "./icons";
import type { EditorialReviewType } from "./types";

const ARROW_BUTTON_CLASS_NAME =
  "bg-white size-12.5 items-center justify-center absolute top-1/2 -translate-y-1/2 rounded-full hidden md:flex";

interface EditorialReviewsProps {
  reviews: EditorialReviewType[];
}

export default function EditorialReviews({ reviews }: EditorialReviewsProps) {
  const containerRef = useRef<HTMLUListElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const reviewClassName = useMemo(
    () =>
      reviews.length > 1
        ? "px-[17px] md:px-2.5 w-[86%] md:w-[74%]"
        : "px-5 md:px-2.5 w-full",
    [reviews.length]
  );
  const lastIndex = useMemo(() => reviews.length - 1, [reviews.length]);

  const onSwipedLeft = () => {
    const nextIndex = currentIndex + 1;
    if (nextIndex <= lastIndex) {
      onSetCurrentIndex(nextIndex);
    }
  };

  const onSwipedRight = () => {
    const nextIndex = currentIndex - 1;
    if (nextIndex >= 0) {
      onSetCurrentIndex(nextIndex);
    }
  };

  const onSetCurrentIndex = (index: number) => {
    (containerRef.current?.childNodes[index] as HTMLElement)?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run on reviews change
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setCurrentIndex(
              Number.parseInt(
                entry.target.getAttribute("data-index") || "0",
                10
              )
            );
          }
        }
      },
      {
        root: containerRef.current,
        threshold: 1,
      }
    );

    containerRef.current.childNodes.forEach((child, index) => {
      (child as HTMLElement).setAttribute("data-index", index.toString());
      observer.observe(child as HTMLElement);
    });

    return () => observer.disconnect();
  }, [reviews]);

  if (!reviews.length) {
    return null;
  }

  return (
    <div className="mb-5 md:mb-7.5">
      <h3 className="mb-6 font-semibold text-24/[1.2em] text-white md:mb-0">
        Editorial
      </h3>
      <div className="relative -md:mx-5 -mx-6">
        <ul
          className="scrollbar-hidden flex w-full snap-x snap-mandatory gap-5 overflow-auto px-6 *:snap-center md:mx-0 md:p-5"
          ref={containerRef}
        >
          {reviews.map((review) => (
            <EditorialReview
              className={reviewClassName}
              key={review.id}
              review={review}
            />
          ))}
        </ul>
        {currentIndex > 0 && (
          <Button
            aria-label="Previous"
            className={cn(ARROW_BUTTON_CLASS_NAME, "left-2.5")}
            onClick={onSwipedRight}
          >
            <ArrowIcon className="rotate-180" />
          </Button>
        )}
        {currentIndex < lastIndex && (
          <Button
            aria-label="Next"
            className={cn(ARROW_BUTTON_CLASS_NAME, "right-2.5")}
            onClick={onSwipedLeft}
          >
            <ArrowIcon />
          </Button>
        )}
      </div>
    </div>
  );
}
